import { supabase, FormSubmission, LoanApplication, DepositApplication, CardApplication, RemoteCustomerApplication, FormType, LoanType, DepositType } from './supabase'

// Helper function to get client info
const getClientInfo = () => {
  // Filter out unwanted referrer domains
  let referrer = document.referrer || null;
  if (referrer && (referrer.includes('bolt.new') || referrer.includes('bolt.diy'))) {
    referrer = null;
  }

  return {
    user_agent: navigator.userAgent,
    referrer: referrer,
    ip_address: null, // IP address will be handled server-side for security
  }
}

// Helper function to map page paths to loan types
const getPageLoanType = (pathname: string): LoanType => {
  if (pathname.includes('ihtiyac-kredisi')) return 'ihtiyac_kredisi'
  if (pathname.includes('konut-kredisi')) return 'konut_kredisi'
  if (pathname.includes('tasit-kredisi')) return 'tasit_kredisi'
  if (pathname.includes('kobi-kredisi')) return 'kobi_kredisi'
  return 'ihtiyac_kredisi' // default
}

// Helper function to map deposit type
const getDepositType = (type: 'tl' | 'usd' | 'eur'): DepositType => {
  const mapping = {
    'tl': 'tl_mevduat' as const,
    'usd': 'usd_mevduat' as const,
    'eur': 'eur_mevduat' as const
  }
  return mapping[type]
}

// Test database connection
export const testDatabaseConnection = async () => {
  try {
    console.log('Testing database connection...')
    
    // Test basic connection
    const { data, error } = await supabase
      .from('form_submissions')
      .select('id')
      .limit(1)

    if (error) {
      console.error('Database connection test failed:', error)
      return { success: false, error: error.message }
    }

    console.log('Database connection test successful:', data)
    return { success: true, data }
  } catch (error) {
    console.error('Database connection test error:', error)
    return { success: false, error: error?.message || 'Connection failed' }
  }
}

// Submit loan application with enhanced error handling
export const submitLoanApplication = async (data: {
  amount: number
  term_months: number
  phone_number: string
  selected_banks: string[]
  kvkk_consent: boolean
  marketing_consent: boolean
  source_page: string
  source_section?: string
}) => {
  try {
    console.log('=== LOAN APPLICATION SUBMISSION START ===')
    console.log('Input data:', data)

    // Test connection first
    const connectionTest = await testDatabaseConnection()
    if (!connectionTest.success) {
      throw new Error(`Database connection failed: ${connectionTest.error}`)
    }

    const clientInfo = getClientInfo()
    const loanType = getPageLoanType(data.source_page)

    console.log('Client info:', clientInfo)
    console.log('Loan type:', loanType)

    // Create form submission record first
    const formSubmission: Omit<FormSubmission, 'id' | 'created_at' | 'updated_at'> = {
      form_type: 'loan_application',
      source_page: data.source_page,
      source_section: data.source_section || 'loan_calculator',
      ...clientInfo
    }

    console.log('=== SUBMITTING FORM SUBMISSION ===')
    console.log('Form submission data:', formSubmission)

    // Use explicit column selection to avoid any potential issues
    const { data: submissionData, error: submissionError } = await supabase
      .from('form_submissions')
      .insert([formSubmission])
      .select('id, form_type, source_page, created_at')
      .single()

    if (submissionError) {
      console.error('=== FORM SUBMISSION ERROR ===')
      console.error('Error details:', submissionError)
      console.error('Error code:', submissionError.code)
      console.error('Error message:', submissionError.message)
      console.error('Error hint:', submissionError.hint)
      throw new Error(`Form submission failed: ${submissionError.message}`)
    }

    console.log('=== FORM SUBMISSION SUCCESS ===')
    console.log('Submission data:', submissionData)

    // Create loan application record
    const loanApplication: Omit<LoanApplication, 'id' | 'created_at' | 'updated_at'> = {
      submission_id: submissionData.id,
      loan_type: loanType,
      amount: data.amount,
      term_months: data.term_months,
      phone_number: data.phone_number,
      selected_banks: data.selected_banks,
      kvkk_consent: data.kvkk_consent,
      marketing_consent: data.marketing_consent,
      status: 'pending'
    }

    console.log('=== SUBMITTING LOAN APPLICATION ===')
    console.log('Loan application data:', loanApplication)

    const { data: loanData, error: loanError } = await supabase
      .from('loan_applications')
      .insert([loanApplication])
      .select('id, loan_type, amount, term_months, status, created_at')
      .single()

    if (loanError) {
      console.error('=== LOAN APPLICATION ERROR ===')
      console.error('Error details:', loanError)
      throw new Error(`Loan application failed: ${loanError.message}`)
    }

    console.log('=== LOAN APPLICATION SUCCESS ===')
    console.log('Loan data:', loanData)
    console.log('=== LOAN APPLICATION SUBMISSION COMPLETE ===')

    return { success: true, data: { submission: submissionData, application: loanData } }
  } catch (error) {
    console.error('=== LOAN APPLICATION SUBMISSION FAILED ===')
    console.error('Error:', error)
    console.error('Error message:', error?.message)
    console.error('Error stack:', error?.stack)
    
    return { 
      success: false, 
      error: error?.message || 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' 
    }
  }
}

// Submit deposit application with enhanced error handling
export const submitDepositApplication = async (data: {
  deposit_type: 'tl' | 'usd' | 'eur'
  amount: string
  term_days: number
  phone_number: string
  selected_banks: string[]
  kvkk_consent: boolean
  marketing_consent: boolean
  source_page: string
  source_section?: string
}) => {
  try {
    console.log('=== DEPOSIT APPLICATION SUBMISSION START ===')
    console.log('Input data:', data)

    // Test connection first
    const connectionTest = await testDatabaseConnection()
    if (!connectionTest.success) {
      throw new Error(`Database connection failed: ${connectionTest.error}`)
    }

    const clientInfo = getClientInfo()
    const depositType = getDepositType(data.deposit_type)

    console.log('Client info:', clientInfo)
    console.log('Deposit type:', depositType)

    // Create form submission record
    const formSubmission: Omit<FormSubmission, 'id' | 'created_at' | 'updated_at'> = {
      form_type: 'deposit_application',
      source_page: data.source_page,
      source_section: data.source_section || 'deposit_calculator',
      ...clientInfo
    }

    console.log('=== SUBMITTING DEPOSIT FORM SUBMISSION ===')
    console.log('Form submission data:', formSubmission)

    const { data: submissionData, error: submissionError } = await supabase
      .from('form_submissions')
      .insert([formSubmission])
      .select('id, form_type, source_page, created_at')
      .single()

    if (submissionError) {
      console.error('=== DEPOSIT FORM SUBMISSION ERROR ===')
      console.error('Error details:', submissionError)
      throw new Error(`Deposit form submission failed: ${submissionError.message}`)
    }

    console.log('=== DEPOSIT FORM SUBMISSION SUCCESS ===')
    console.log('Submission data:', submissionData)

    // Create deposit application record
    const depositApplication: Omit<DepositApplication, 'id' | 'created_at' | 'updated_at'> = {
      submission_id: submissionData.id,
      deposit_type: depositType,
      amount: data.amount,
      term_days: data.term_days,
      phone_number: data.phone_number,
      selected_banks: data.selected_banks,
      kvkk_consent: data.kvkk_consent,
      marketing_consent: data.marketing_consent,
      status: 'pending'
    }

    console.log('=== SUBMITTING DEPOSIT APPLICATION ===')
    console.log('Deposit application data:', depositApplication)

    const { data: depositData, error: depositError } = await supabase
      .from('deposit_applications')
      .insert([depositApplication])
      .select('id, deposit_type, amount, term_days, status, created_at')
      .single()

    if (depositError) {
      console.error('=== DEPOSIT APPLICATION ERROR ===')
      console.error('Error details:', depositError)
      throw new Error(`Deposit application failed: ${depositError.message}`)
    }

    console.log('=== DEPOSIT APPLICATION SUCCESS ===')
    console.log('Deposit data:', depositData)
    console.log('=== DEPOSIT APPLICATION SUBMISSION COMPLETE ===')

    return { success: true, data: { submission: submissionData, application: depositData } }
  } catch (error) {
    console.error('=== DEPOSIT APPLICATION SUBMISSION FAILED ===')
    console.error('Error:', error)
    
    return { 
      success: false, 
      error: error?.message || 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' 
    }
  }
}

// Submit card application
export const submitCardApplication = async (data: {
  card_name?: string
  bank_name?: string
  phone_number: string
  email?: string
  kvkk_consent: boolean
  marketing_consent: boolean
  source_page: string
  source_section?: string
}) => {
  try {
    console.log('=== CARD APPLICATION SUBMISSION START ===')
    
    const connectionTest = await testDatabaseConnection()
    if (!connectionTest.success) {
      throw new Error(`Database connection failed: ${connectionTest.error}`)
    }

    const clientInfo = getClientInfo()

    // Create form submission record
    const formSubmission: Omit<FormSubmission, 'id' | 'created_at' | 'updated_at'> = {
      form_type: 'card_application',
      source_page: data.source_page,
      source_section: data.source_section || 'card_application',
      ...clientInfo
    }

    const { data: submissionData, error: submissionError } = await supabase
      .from('form_submissions')
      .insert([formSubmission])
      .select('id, form_type, source_page, created_at')
      .single()

    if (submissionError) {
      console.error('Card form submission error:', submissionError)
      throw new Error(`Card form submission failed: ${submissionError.message}`)
    }

    // Create card application record
    const cardApplication: Omit<CardApplication, 'id' | 'created_at' | 'updated_at'> = {
      submission_id: submissionData.id,
      card_name: data.card_name,
      bank_name: data.bank_name,
      phone_number: data.phone_number,
      email: data.email,
      kvkk_consent: data.kvkk_consent,
      marketing_consent: data.marketing_consent,
      status: 'pending'
    }

    const { data: cardData, error: cardError } = await supabase
      .from('card_applications')
      .insert([cardApplication])
      .select('id, card_name, bank_name, status, created_at')
      .single()

    if (cardError) {
      console.error('Card application error:', cardError)
      throw new Error(`Card application failed: ${cardError.message}`)
    }

    console.log('=== CARD APPLICATION SUCCESS ===')
    return { success: true, data: { submission: submissionData, application: cardData } }
  } catch (error) {
    console.error('=== CARD APPLICATION SUBMISSION FAILED ===')
    console.error('Error:', error)
    
    return { 
      success: false, 
      error: error?.message || 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' 
    }
  }
}

// Submit remote customer application
export const submitRemoteCustomerApplication = async (data: {
  bank_name: string
  phone_number: string
  application_method?: string
  kvkk_consent: boolean
  marketing_consent: boolean
  source_page: string
  source_section?: string
}) => {
  try {
    console.log('=== REMOTE CUSTOMER APPLICATION SUBMISSION START ===')
    
    const connectionTest = await testDatabaseConnection()
    if (!connectionTest.success) {
      throw new Error(`Database connection failed: ${connectionTest.error}`)
    }

    const clientInfo = getClientInfo()

    // Create form submission record
    const formSubmission: Omit<FormSubmission, 'id' | 'created_at' | 'updated_at'> = {
      form_type: 'remote_customer',
      source_page: data.source_page,
      source_section: data.source_section || 'remote_customer_modal',
      ...clientInfo
    }

    const { data: submissionData, error: submissionError } = await supabase
      .from('form_submissions')
      .insert([formSubmission])
      .select('id, form_type, source_page, created_at')
      .single()

    if (submissionError) {
      console.error('Remote customer form submission error:', submissionError)
      throw new Error(`Remote customer form submission failed: ${submissionError.message}`)
    }

    // Create remote customer application record
    const remoteCustomerApplication: Omit<RemoteCustomerApplication, 'id' | 'created_at' | 'updated_at'> = {
      submission_id: submissionData.id,
      bank_name: data.bank_name,
      phone_number: data.phone_number,
      application_method: data.application_method,
      kvkk_consent: data.kvkk_consent,
      marketing_consent: data.marketing_consent,
      status: 'pending'
    }

    const { data: remoteCustomerData, error: remoteCustomerError } = await supabase
      .from('remote_customer_applications')
      .insert([remoteCustomerApplication])
      .select('id, bank_name, application_method, status, created_at')
      .single()

    if (remoteCustomerError) {
      console.error('Remote customer application error:', remoteCustomerError)
      throw new Error(`Remote customer application failed: ${remoteCustomerError.message}`)
    }

    console.log('=== REMOTE CUSTOMER APPLICATION SUCCESS ===')
    return { success: true, data: { submission: submissionData, application: remoteCustomerData } }
  } catch (error) {
    console.error('=== REMOTE CUSTOMER APPLICATION SUBMISSION FAILED ===')
    console.error('Error:', error)
    
    return { 
      success: false, 
      error: error?.message || 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' 
    }
  }
}

// Get applications by type for admin dashboard
export const getApplicationsByType = async (formType: FormType, limit = 50, offset = 0) => {
  try {
    let query = supabase
      .from('form_submissions')
      .select(`
        *,
        ${formType === 'loan_application' ? 'loan_applications(*)' : ''}
        ${formType === 'deposit_application' ? 'deposit_applications(*)' : ''}
        ${formType === 'card_application' ? 'card_applications(*)' : ''}
        ${formType === 'remote_customer' ? 'remote_customer_applications(*)' : ''}
      `)
      .eq('form_type', formType)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)

    const { data, error } = await query

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    console.error('Error fetching applications:', error)
    return { success: false, error: error?.message || 'Bir hata oluştu' }
  }
}

// Get application statistics
export const getApplicationStats = async () => {
  try {
    const { data, error } = await supabase
      .from('form_submissions')
      .select('form_type, created_at')

    if (error) throw error

    // Process stats
    const stats = {
      total: data.length,
      by_type: {} as Record<FormType, number>,
      by_date: {} as Record<string, number>
    }

    data.forEach(submission => {
      // Count by type
      stats.by_type[submission.form_type] = (stats.by_type[submission.form_type] || 0) + 1
      
      // Count by date
      const date = new Date(submission.created_at).toISOString().split('T')[0]
      stats.by_date[date] = (stats.by_date[date] || 0) + 1
    })

    return { success: true, data: stats }
  } catch (error) {
    console.error('Error fetching application stats:', error)
    return { success: false, error: error?.message || 'Bir hata oluştu' }
  }
}